import React, { useMemo, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert } from '@ds/react-components';
import { numberToCurrency, isNotEmptyArray } from 'utils/misc';
import cancellationTypes from 'constants/cancellationTypes';
import OverdueForm from './OverdueForm';
import NoOverdueForm from './NoOverdueForm';
import CancellationInfo from './CancellationInfo';
import { getCancelDaysNotice } from './common/cancellationUtils';

import * as Styled from './CancellationForm.styled';

const CancellationForm = (props) => {
  const {
    cancellationReasons,
    account,
    localisation,
    adfitNo,
    cancelAccount,
    pendingPaymentAmount,
    estimatedFinalBillDate,
    outstandingDsFee,
    balanceToSettleWithoutCancelFee,
    facility,
    permissions,
  } = props;
  const { overdueAmount, country, daysNotice } = account;

  const [isUpdatingAccount, setIsUpdatingAccount] = useState(false);

  const [submissionError, setSubmissionError] = useState('');

  // The data is used to show the summary of the cancellation once the form is submitted.
  const [submittedInfo, setSubmittedInfo] = useState(null);

  const cancelDaysNotice = getCancelDaysNotice(daysNotice, country);

  const onFormSubmit = useCallback(
    async (cancellationOptions, data) => {
      setIsUpdatingAccount(true);
      setSubmissionError('');

      //Add any missing values.
      const formattedData = {
        ...data,
        cancelReason: !data.cancelReason
          ? 'Facility Request'
          : data.cancelReason,
        //server does not accept a zero value cancellation fee.
        cancellationFee:
          data.cancellationOption === cancellationTypes.COLLECT_OVERDUE &&
          !data.cancellationFee
            ? undefined
            : data.cancellationFee,
        //passing true to toIsoString method makes sure that the date is not converted to utc format.
        noticeStartDate:
          data.noticeStartDate && data.noticeStartDate.toISOString(true),
        cancelDaysNotice,
      };

      try {
        await cancelAccount(adfitNo, formattedData);
        setSubmittedInfo({
          ...formattedData,
          cancellationOption: cancellationOptions.find(
            (option) => option.value === data.cancellationOption
          ),
        });
      } catch (err) {
        setSubmissionError(
          err.message || 'Something went wrong. Please try again.'
        );
        setIsUpdatingAccount(false);
        window.scroll(0, 0);
      }
    },
    [
      cancelAccount,
      adfitNo,
      setSubmissionError,
      setIsUpdatingAccount,
      cancelDaysNotice,
    ]
  );

  const hasPendingPaymentAmount = useMemo(() => {
    return pendingPaymentAmount !== 0;
  }, [pendingPaymentAmount]);

  const noteAlerts = useMemo(() => {
    const getOverdueAlert = () =>
      overdueAmount > 0
        ? [
            `This account has an overdue amount of ${numberToCurrency(
              overdueAmount,
              localisation
            )}.`,
          ]
        : [];

    const getRecentPayAlert = () => {
      return hasPendingPaymentAmount
        ? [
            `Total payment of ${numberToCurrency(
              Math.abs(pendingPaymentAmount),
              localisation
            )} was attempted for this customer and is pending.`,
          ]
        : [];
    };

    const noteAlerts = [...getOverdueAlert(), ...getRecentPayAlert()];
    return isNotEmptyArray(noteAlerts) ? noteAlerts : null;
  }, [overdueAmount, localisation, hasPendingPaymentAmount]);

  if (submittedInfo) {
    return (
      <CancellationInfo
        cancellationOption={submittedInfo.cancellationOption}
        cancellationFee={submittedInfo.cancellationFee}
        cancelDaysNotice={submittedInfo.cancelDaysNotice}
        noticeStartDate={submittedInfo.noticeStartDate}
        account={account}
        adfitNo={adfitNo}
        localisation={localisation}
      />
    );
  }

  return (
    <Styled.FormContainer data-testid={'cancellation-form'}>
      {!!submissionError && (
        <Styled.InfoAlert variant={'error'} role={'banner'} dismissible={false}>
          <Alert.AlertMessage data-testid={`submission-error`}>
            {submissionError}
          </Alert.AlertMessage>
        </Styled.InfoAlert>
      )}
      {!!noteAlerts && (
        <Styled.InfoAlert variant={'warning'} dismissible={false}>
          {noteAlerts.map((alertMessage, index) => (
            <Alert.AlertMessage
              data-testid={`alert-message-${index}`}
              key={index}>{`${
              noteAlerts.length > 1 ? '• ' : ''
            } ${alertMessage}`}</Alert.AlertMessage>
          ))}
        </Styled.InfoAlert>
      )}
      {/*Though the below two forms might seem very similar in look and feel, there are too many implementation differences hence treating them as separate forms*/}
      {overdueAmount <= 0 ? (
        <NoOverdueForm
          cancellationReasons={cancellationReasons}
          localisation={localisation}
          onFormSubmit={onFormSubmit}
          isUpdatingAccount={isUpdatingAccount}
          adfitNo={adfitNo}
          estimatedFinalBillDate={estimatedFinalBillDate}
          recentNotClearedPayment={!!hasPendingPaymentAmount}
          account={account}
          balanceToSettleWithoutCancelFee={balanceToSettleWithoutCancelFee}
          facility={facility}
          cancelDaysNotice={cancelDaysNotice}
          permissions={permissions}
        />
      ) : (
        <OverdueForm
          cancellationReasons={cancellationReasons}
          localisation={localisation}
          onFormSubmit={onFormSubmit}
          isUpdatingAccount={isUpdatingAccount}
          adfitNo={adfitNo}
          account={account}
          recentNotClearedPayment={!!hasPendingPaymentAmount}
          estimatedFinalBillDate={estimatedFinalBillDate}
          outstandingDsFee={outstandingDsFee}
          balanceToSettleWithoutCancelFee={balanceToSettleWithoutCancelFee}
          facility={facility}
          cancelDaysNotice={cancelDaysNotice}
          permissions={permissions}
        />
      )}
    </Styled.FormContainer>
  );
};

CancellationForm.propTypes = {
  cancellationReasons: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ).isRequired,
  account: PropTypes.object.isRequired,
  transactions: PropTypes.arrayOf(
    PropTypes.shape({
      amount: PropTypes.number.isRequired,
      cleared: PropTypes.bool,
    })
  ).isRequired,
  facility: PropTypes.shape({
    name: PropTypes.string,
    industryType: PropTypes.string.isRequired,
  }).isRequired,
  localisation: PropTypes.object.isRequired,
  adfitNo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  estimatedFinalBillDate: PropTypes.string,
  outstandingDsFee: PropTypes.number,
  cancelAccount: PropTypes.func.isRequired,
  permissions: PropTypes.arrayOf(PropTypes.string).isRequired,
  balanceToSettleWithoutCancelFee: PropTypes.number,
};

CancellationForm.defaultProps = {
  outstandingDsFee: null,
  balanceToSettleWithoutCancelFee: null,
};

export default CancellationForm;
